import { useState } from "react";
import SalesforceIntegration from "./salesforce-integration";
import HubspotIntegration from "./hubspot-integration";
import ZohoIntegration from "./zoho-integration";
import { ChevronDown, ChevronRight } from "react-feather";
import { getApiUrl } from "../../../../utils/baseUrl";
import axios from "axios";
import { toast } from "react-toastify";

const crmTypes = {
  salesforce: "Salesforce",
  hubspot: "Hubspot",
  zoho: "Zoho",
  request: "Request CRM",
};

const crmLogo = {
  salesforce: "https://res.cloudinary.com/drzta9shk/image/upload/v1681909881/intel/icons/salesforce_icon_l8arax.svg",
  hubspot: "https://res.cloudinary.com/drzta9shk/image/upload/v1681824284/intel/icons/hubspot_logo_ykc5rk.svg",
  zoho: "https://res.cloudinary.com/drzta9shk/image/upload/v1692170660/intel/icons/ZOHO_color_logo_uqeuep.svg",
};

export default function CrmIntegration({ location, userDetails }) {
  const [expandedCrms, setExpandedCrms] = useState([]);
  const [requestedCrm, setRequestedCrm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [salesforceLoader, setSalesforceLoader] = useState(false);
  const [hubspotLoader, setHubspotLoader] = useState(false);
  const [zohoLoader, setZohoLoader] = useState(false);

  const handleSubmitCrmRequest = async () => {
    if (!requestedCrm.trim()) return;
    setIsSubmitting(true);
    try {
      await axios.post(getApiUrl("userCrmData"), {
        crm_details: requestedCrm.trim(),
        tabCategory: "Configuration",
      });
      setRequestedCrm("");
      setExpandedCrms(expandedCrms.filter((crm) => crm !== crmTypes.request));
      toast.success("Thanks for showing your interest in the CRM. Your customer success manager will reach out to you at the earliest to discuss more in detail");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCrmStatus = (crmType) => {
    if (!userDetails) return "";
    switch (crmType) {
      case crmTypes.salesforce:
        return userDetails.is_salesforce_active ? "Integrated" : "";
      case crmTypes.hubspot:
        return userDetails.is_hubspot_active ? "Integrated" : "";
      case crmTypes.zoho:
        return userDetails.is_zoho_active ? "Integrated" : "";
      default:
        return "";
    }
  };

  const crmItems = [
    { type: crmTypes.salesforce, logo: crmLogo.salesforce },
    { type: crmTypes.hubspot, logo: crmLogo.hubspot },
    { type: crmTypes.zoho, logo: crmLogo.zoho },
  ].map((item) => ({
    ...item,
    status: getCrmStatus(item.type),
  }));

  const handleCrmClick = (crmType) => {
    if (crmType === "Salesforce") {
      setSalesforceLoader(true);
    } else if (crmType === "Hubspot") {
      setHubspotLoader(true);
    } else if (crmType === "Zoho") {
      setZohoLoader(true);
    }
    setExpandedCrms((prevExpandedCrms) => (prevExpandedCrms.includes(crmType) ? prevExpandedCrms.filter((type) => type !== crmType) : [...prevExpandedCrms, crmType]));
  };

  const renderCrmComponent = (type) => {
    switch (type) {
      case crmTypes.salesforce:
        return <SalesforceIntegration userDetails={userDetails} location={location} loader={salesforceLoader} setLoader={setSalesforceLoader} />;
      case crmTypes.hubspot:
        return <HubspotIntegration userDetails={userDetails} location={location} loader={hubspotLoader} setLoader={setHubspotLoader} />;
      case crmTypes.zoho:
        return <ZohoIntegration userDetails={userDetails} location={location} loader={zohoLoader} setLoader={setZohoLoader} />;
      default:
        return null;
    }
  };

  return (
    <div className="container" style={{ height: "89vh", overflow: "auto" }}>
      <div className="mb-3 mt-2" id="crm-integration-container">
        <div className="crm-header">Integrate your CRM with Trademo</div>
        <div className="crm-sub-header">Streamline your workflow by connecting the CRM your team uses</div>
      </div>
      <div
        className="d-flex align-items-start mx-auto justify-content-center"
        style={{
          gap: "16px",
        }}
      >
        {crmItems.slice(0, 2).map((item, index) => (
          <div key={index} className="mb-3 crm-container">
            <div className="crm-item cursor-pointer" onClick={() => handleCrmClick(item.type)}>
              <div className="d-flex align-items-center gap-2">
                <img src={item.logo} alt={`${item.type} logo`} className="crm-logo" />
                <span className="crm-name">{item.type}</span>
                {item.status && <span className="crm-status">{item.status}</span>}
              </div>
              <div className="crm-arrow">
                {expandedCrms.includes(item.type) ? <ChevronDown className="ml-1" color="#8C8C8C" size={20} /> : <ChevronRight className="ml-1" color="#8C8C8C" size={20} />}
              </div>
            </div>
            {expandedCrms.includes(item.type) && <div>{renderCrmComponent(item.type)}</div>}
          </div>
        ))}
      </div>
      <div
        className="d-flex align-items-start mx-auto justify-content-center"
        style={{
          gap: "16px",
        }}
      >
        {crmItems.slice(2).map((item, index) => (
          <div key={index} className="mb-3 crm-container">
            <div className="crm-item cursor-pointer" onClick={() => handleCrmClick(item.type)}>
              <div className="d-flex align-items-center gap-2">
                <img src={item.logo} alt={`${item.type} logo`} className="crm-logo" />
                <span className="crm-name">{item.type}</span>
                {item.status && <span className="crm-status">{item.status}</span>}
              </div>
              <div className="crm-arrow">
                {expandedCrms.includes(item.type) ? <ChevronDown className="ml-1" color="#8C8C8C" size={20} /> : <ChevronRight className="ml-1" color="#8C8C8C" size={20} />}
              </div>
            </div>
            {expandedCrms.includes(item.type) && <div>{renderCrmComponent(item.type)}</div>}
          </div>
        ))}
        <div className="mb-3 crm-container">
          <div
            className="crm-item cursor-pointer"
            onClick={() => handleCrmClick(crmTypes.request)}
            style={{
              height: "72px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <span className="crm-name ml-0">{crmTypes.request}</span>
            </div>
            <div className="crm-arrow">
              {expandedCrms.includes(crmTypes.request) ? <ChevronDown className="ml-1" color="#8C8C8C" size={20} /> : <ChevronRight className="ml-1" color="#8C8C8C" size={20} />}
            </div>
          </div>
          {expandedCrms.includes(crmTypes.request) && (
            <div className="p-3">
              <input type="text" className="form-control" placeholder="Enter your CRM" value={requestedCrm} onChange={(e) => setRequestedCrm(e.target.value)} />
              <button className="btn btn-primary request-crm-button mt-3" disabled={!requestedCrm.trim() || isSubmitting} onClick={handleSubmitCrmRequest}>
                Submit
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
